import { useQuery } from '@tanstack/react-query';
import {
  bidKeys,
  type ApplicationGateApi,
  type ApplicationGateReason,
} from '@tempmee/awards';
import { useAppSelector } from '~/hooks';
import { getApplicationQuestionsService } from '../services/GetApplicationQuestions';

/**
 * Application gate + form for a cycle. `eligible` is `undefined` while the
 * gate is loading, then the server's verdict; `ineligibleReason` is set only
 * when `eligible` is `false`. Submitting invalidates `bidKeys.all`
 * (useSubmitBidApplication), which re-gates this query to `already-applied`.
 */
export const useGetApplicationQuestions = (cycleId: string | undefined) => {
  // Eligibility is per office (x-office header) — scope the cache so a DSO
  // admin switching child offices re-gates instead of reusing the verdict.
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);

  const { data, isLoading, isError, refetch } = useQuery<ApplicationGateApi>({
    queryKey: [...bidKeys.all, 'applicationQuestions', cycleId, selectedOfficeId],
    queryFn: () => getApplicationQuestionsService(cycleId as string),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!cycleId,
  });

  return {
    questions: data?.eligible ? data.questions : [],
    eligible: data?.eligible,
    ineligibleReason: (data && !data.eligible
      ? data.reason
      : undefined) as ApplicationGateReason | undefined,
    isLoading,
    isError,
    refetch,
  };
};
